import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  TextField,
  Button,
  Box,
  Dialog,
  Grid,
  DialogActions,
  Stack, 
  Select,
  MenuItem,
  Pagination,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { saveSmsTemplateAction, getSmsTemplateAction, deleteSmsTemplateAction, searchSmsTemplateAction } from '../../../redux/actions/templateActions';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from "@mui/material/styles";
import Loader from '../../loader/loader';
import dayjs from "dayjs";
import { CustomTableCell } from '../../../common/CustomTableCell';
import NoDataFound from '../../../common/NoDataFound';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#1565c0',
  color: theme.palette.common.white,
  fontFamily: '"Poppins", sans-serif',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& td, & th': {
    padding: theme.spacing(1.5),
    fontFamily: '"Poppins", sans-serif',
  },
}));

const Template = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [open, setOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchPayload, setSearchPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const dispatch = useDispatch();
  const get_all_template = useSelector((state) => state?.templateReducer?.all_sms_template?.content || []);
  const get_all_template_pagination = useSelector((state) => state?.templateReducer?.all_sms_template || []);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Use ref to track if API call is in progress to prevent duplicate calls
  const apiCallInProgress = useRef(false);
  const mounted = useRef(true);

  // Centralized API call function to prevent duplicates
  const fetchData = useCallback(async (pageNum = page, rowsNum = rowsPerPage, searchQuery = null) => {
    if (apiCallInProgress.current || !mounted.current) return;
    
    apiCallInProgress.current = true;
    setLoading(true);

    try {
      if (searchQuery) {
        await dispatch(searchSmsTemplateAction(searchQuery, pageNum, rowsNum));
      } else {
        await dispatch(getSmsTemplateAction(pageNum, rowsNum));
      }
      setHasInitialLoad(true);
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
      apiCallInProgress.current = false;
    }
  }, [dispatch, page, rowsPerPage]);

  // Initial load effect
  useEffect(() => {
    mounted.current = true;
    // fetchData();
    
    return () => {
      mounted.current = false;
    };
  }, []); // Remove page and rowsPerPage dependencies to prevent auto-refetch

  const [formValues, setFormValues] = useState({
    title: '',
    text: '',
  });

  const [errors, setErrors] = useState({
    title: '',
    text: ''
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateForm = () => {
    let tempErrors = {
      title: '',
      text: ''
    };
    let isValid = true;

    if (!formValues.title.trim()) {
      tempErrors.title = 'Title is required';
      isValid = false;
    }

    if (!formValues.text.trim()) {
      tempErrors.text = 'Task Description is required';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setOpen(false);
    setLoading(true);
    
    try {
      await dispatch(saveSmsTemplateAction(formValues));
      
      // Refresh data after successful save/update
      await fetchData(page, rowsPerPage, isSearchActive ? searchPayload : null);
      
      setSnackbar({
        open: true,
        message: isEditMode ? 'Template updated successfully' : 'Template saved successfully',
        severity: 'success',
      });
      
      // Reset form
      setIsEditMode(false);
      setFormValues({ title: '', text: '' });
      setIsSearchActive(false);
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: isEditMode ? 'Failed to update template' : 'Failed to save template',
        severity: 'error',
      });
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    setIsEditMode(false);
    setFormValues({
      title: '',
      text: '',
    });
    setErrors({
      title: '',
      text: ''
    });
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
    setFormValues({
      title: '',
      text: '',
    });
    setErrors({
      title: '',
      text: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEditClick = (template) => {
    const templateToEdit = get_all_template.find(item => item.id === template.id);
    if (templateToEdit) {
      setFormValues({
        id: templateToEdit.id,
        title: templateToEdit.title,
        text: templateToEdit.text,
      });
      setIsEditMode(true);
      setOpen(true);
    }
  };

  const handleDeleteClick = (id) => {
    setTemplateToDelete(id);
    setIsDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    setLoading(true);
    try {
      await dispatch(deleteSmsTemplateAction(templateToDelete));
      
      // Refresh data after successful deletion
      await fetchData(page, rowsPerPage, isSearchActive ? searchPayload : null);
      
      setSnackbar({
        open: true,
        message: 'Template deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete template',
        severity: 'error',
      });
      setLoading(false);
    } finally {
      setIsDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDialogOpen(false);
    setTemplateToDelete(null);
  };

  // Optimized pagination handler - only update state, let fetchData handle API
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage, isSearchActive ? searchPayload : null);
  };

  // Optimized rows per page handler
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(0, newRowsPerPage, isSearchActive ? searchPayload : null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    const trimmedSearchTerm = searchTerm.trim();
  
    if (trimmedSearchTerm === '') {
      // If search term is empty, reset to normal view
      setIsSearchActive(false);
      setSearchPayload(null);
      return;
    }
  
    // Set search active and fetch search results
    setSearchPayload(trimmedSearchTerm);
    setIsSearchActive(true);
    setPage(0);
    fetchData(0, rowsPerPage, trimmedSearchTerm);
  };
  
  const handleReset = () => {
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm === '') {
      // If search term is empty, reset to normal view
      setIsSearchActive(false);
      setSearchPayload(null);
      return;
    }
    setSearchTerm('');
    setIsSearchActive(false);
    setSearchPayload(null);
    setPage(0);
    fetchData(0, rowsPerPage, null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Helper function to truncate text for mobile view
  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Box sx={{ 
      padding: isMobile ? 2 : 3,
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <Paper sx={{ 
        padding: isMobile ? 2 : 3,
        boxShadow: isMobile ? 1 : 3,
        border: "1px solid rgba(49, 49, 49, 0.1)"
      }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          marginBottom: isMobile ? 2 : 3,
          gap: isMobile ? 2 : 0
        }}>
           <Typography
                      variant="h2"
                      sx={{
                        ml: 1,
                        mb: 2,
                      }}
                    >
            {isSearchActive 
              ? `All Template (${get_all_template_pagination.totalElements || 0})`
              : 'All Template'
            }
          </Typography>
          <Button
             variant="contained"
            onClick={handleClickOpen}
          >
            Add Template
          </Button>
        </Box>

        {/* Search Section */}
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: isMobile ? 1 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            width: isMobile ? '100%' : 'auto'
          }}>  
          <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "19.6px",
                    textAlign: "left",
                    marginBottom: "4px",
                  }}
                >
                  Search Term
                </Typography>         
            <TextField
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              sx={{ 
                width: isMobile ? '100%' : '250px',
                '& .MuiOutlinedInput-root': {
                  height: isMobile ? '45px' : '55px'
                },
                '& .MuiInputBase-input': {
                  fontSize: isMobile ? '14px' : '16px',
                  padding: isMobile ? '0 12px' : '0 14px'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize={isMobile ? "small" : "medium"} /> 
                  </InputAdornment>
                ),
              }}
              placeholder="Search templates..."
            />
          </Box>

          <Box sx={{
            display: 'flex',
            gap: 1,
            width: isMobile ? '100%' : 'auto',
            flexDirection: isMobile ? 'row' : 'row'
          }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>
        </Box>
        </Box>

        {/* Add/Edit Template Dialog */}
        <Dialog 
          open={open}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            handleClose(event, reason);
          }}
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          disableScrollLock={true}
          keepMounted={false}
          hideBackdrop={false}
          style={{ zIndex: 1300 }}
          PaperProps={{
            sx: {
              margin: isMobile ? 0 : 3,
              width: isMobile ? '100%' : 'auto',
              height: isMobile ? '100%' : 'auto'
            }
          }}
        >
          <DialogTitle
            className="btn-primary"
            sx={{
              height: isMobile ? '56px' : '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 12px' : '0 16px',
              textTransform: 'none',
              fontSize: isMobile ? '16px' : '18px'
            }}
          >
            {isEditMode ? 'Update Template' : 'Add Template'}
          </DialogTitle>

          <DialogContent sx={{ padding: isMobile ? '16px' : '24px' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1" sx={{ 
                  mb: 1, 
                  mt: isMobile ? 2 : 3,
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  Title
                </Typography>
                <TextField
                  name="title"
                  id="title-field"
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  value={formValues.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: isMobile ? '14px' : '16px'
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body1" sx={{ 
                  mb: 1,
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  Task Description
                </Typography>
                <TextField
                  name="text"
                  id="text-field"
                  fullWidth
                  multiline
                  rows={isMobile ? 4 : 6}
                  size={isMobile ? "small" : "medium"}
                  value={formValues.text}
                  onChange={handleChange}
                  error={!!errors.text}
                  helperText={errors.text}
                  required
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: isMobile ? '14px' : '16px'
                    }
                  }}
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ 
            padding: isMobile ? '12px 16px' : '16px 24px',
            gap: 1,
            flexDirection: isMobile ? 'column-reverse' : 'row'
          }}>
            <Button 
              onClick={handleClose}
              variant='outlined' 
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              variant='outlined' 
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Conditional Content Display - Following DashboardTypeOfTaskOpen pattern */}
        {!isSearchActive ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              discription="Please use the search filters above to fetch and display templates."
              message="Use search or click 'Add Template' to create new templates"
            />
          </Box>
        ) : get_all_template.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              title="No Data Found"
              discription="No templates found matching your search criteria."
              message="Try adjusting your search terms or create a new template"
            />
          </Box>
        ) : (
          // Show table when data exists
          <TableContainer 
            component={Paper}
            sx={{ 
              position: 'relative', 
              minHeight: '200px',
              maxHeight: isMobile ? '60vh' : 'none', 
              overflow: 'auto',
              boxShadow: isMobile ? 1 : 2
            }}
          >
            <Table
              stickyHeader
              sx={{ 
                minWidth: isMobile ? '800px' : '900px',
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '11px' : '14px',
                  padding: isMobile ? '6px 8px' : '12px 16px',
                  whiteSpace: isMobile ? 'nowrap' : 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '120px' : '200px'
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '100px' : '150px'
                    }}
                  >
                    Title
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '120px' : '200px'
                    }}
                  >
                    Description
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Date
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '100px' : '140px'
                    }}
                  >
                    Updated Date
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Updated By
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '120px'
                    }}
                  >
                    Created By
                  </StyledTableCell>
                  <StyledTableCell 
                    className="styled-table-heading"
                    align="right"
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '80px' : '100px'
                    }}
                  >
                    Action
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                        <Loader />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : get_all_template.map((row, index) => (
                  <StyledTableRow key={row.id || index} className="styled-table-row">
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={row.title || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(row.title, 12)}</span>
                        </Tooltip>
                      ) : (
                        row.title || "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={row.text || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(row.text, 15)}</span>
                        </Tooltip>
                      ) : (
                        row.text || "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.createdBy?.timestamp ? dayjs(row.createdBy.timestamp).format("MM-DD-YYYY") : '-'}
                    </TableCell>
                    <TableCell>
                      <CustomTableCell timestamp={row.updatedBy?.timestamp} />
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={row.updatedBy?.name || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(row.updatedBy?.name || '-', 10)}</span>
                        </Tooltip>
                      ) : (
                        row.updatedBy?.name || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={row.createdBy?.name || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(row.createdBy?.name || '-', 10)}</span>
                        </Tooltip>
                      ) : (
                        row.createdBy?.name || '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton 
                          size={isMobile ? "small" : "medium"} 
                          onClick={() => handleEditClick(row)}
                          disabled={loading}
                          sx={{ p: isMobile ? '4px' : '8px' }}
                        >
                          <EditIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                        <IconButton 
                          size={isMobile ? "small" : "medium"} 
                          onClick={() => handleDeleteClick(row.id)}
                          disabled={loading}
                          sx={{ p: isMobile ? '4px' : '8px' }}
                        >
                          <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination Section */}
            {get_all_template_pagination.totalElements > 0 && (
              <Box sx={{ 
                width: "100%", 
                overflow: "hidden", 
                padding: isMobile ? "12px 0" : "16px 0" 
              }}>
                <Stack
                  direction={isMobile ? "column" : "row"}
                  justifyContent="flex-end"
                  alignItems="center"
                  flexWrap="wrap"
                  spacing={2}
                  sx={{
                    gap: isMobile ? 1 : 2
                  }}
                >
                  {/* Rows per page dropdown */}
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ 
                      fontSize: isMobile ? "11px" : "14px", 
                      marginRight: "8px" 
                    }}>
                      Rows per page:
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      disabled={loading}
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        height: isMobile ? '32px' : '36px',
                        fontSize: isMobile ? '11px' : '14px',
                        minWidth: isMobile ? '60px' : '80px',
                        '& .MuiSelect-select': {
                          padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                        }
                      }}
                    >
                      {(get_all_template_pagination.totalPages === 0
                        ? []
                        : [5, 10, 15, 25, 50, 100]
                      ).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Pagination controls */}
                  <Pagination
                    count={Math.ceil(get_all_template_pagination.totalElements / rowsPerPage)}
                    page={page + 1}
                    onChange={(event, value) => handleChangePage(event, value - 1)}
                    disabled={loading}
                    showFirstButton
                    showLastButton
                    size={isMobile ? "small" : "medium"}
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: isMobile ? '12px' : '14px',
                        minWidth: isMobile ? '28px' : '32px',
                        height: isMobile ? '28px' : '32px'
                      }
                    }}
                  />
                </Stack>
              </Box>
            )}
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={isDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCancelDelete(event, reason);
        }}
        maxWidth="sm"
        fullWidth={isMobile}
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 3,
            width: isMobile ? 'calc(100% - 16px)' : 'auto'
          }
        }}
      >
        <DialogTitle 
          className="btn-primary"
          sx={{
            height: isMobile ? '56px' : '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '0 12px' : '0 16px',
            fontSize: isMobile ? '16px' : '18px'
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{
            marginTop: '15px',
            fontSize: isMobile ? '14px' : '16px'
          }}>
           Are you sure you want to delete this template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          padding: isMobile ? '12px 16px' : '16px 24px',
          gap: 1
        }}>
          <Button 
            onClick={handleCancelDelete}
            variant='outlined' 
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant='outlined' 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: 'top', 
          horizontal: isMobile ? 'center' : 'right'
        }}
        sx={{
          top: isMobile ? '60px' : '24px',
          '& .MuiSnackbarContent-root': {
            fontSize: isMobile ? '12px' : '14px'
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            whiteSpace: 'pre-line',
            fontSize: isMobile ? '12px' : '14px',
            width: isMobile ? '90vw' : 'auto',
            maxWidth: isMobile ? '350px' : '500px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Template;